import Dexie from "dexie";

/**
 * The databases holding the previous user's round data, ordered
 * REBUILDABLE-FIRST so a partial failure can never destroy the irreplaceable
 * one before we are committed to signing out.
 *
 * Membership rule: every database the Lockdown persist config mirrors to a
 * native file belongs here. Sign-out promises the durable copy is REMOVED
 * (hard-fail: a failed delete blocks the redirect) — a durable store deleted
 * only in the best-effort batch can outlive sign-out on disk with the
 * previous user's data.
 *
 * `ClientInfoStore` is proposal text and `ArtifactQueryCache` is the
 * AI-artifact snapshot (meetings/clips catalogs, summaries) — both
 * re-downloaded from the server on the next load, so losing them costs
 * nothing. `SPAStorage` holds the queued offline edits
 * (`unconfirmedActions2`), which exist nowhere else until they sync. It must
 * therefore be deleted LAST: if an earlier delete fails we bail with the
 * queue still on disk, and invariant 9's "reload rebuilds from the files still
 * on disk" actually holds.
 */
export const USER_DATA_DATABASES_REBUILDABLE_FIRST = [
  "ClientInfoStore",
  // The artifact-cache database name is a literal, NOT an import of
  // queryClientPersister's constant: this module sits in the sign-out page's
  // import graph, and pulling the persister (and everything it evaluates)
  // into that graph at module-load reordered app-module initialization enough
  // to trip a latent import-cycle TDZ in CI. The coupling is enforced instead
  // by lockdownArtifactCache.test.ts, which fails if this literal drifts from
  // the persister's exported name or the Lockdown persist config.
  "ArtifactQueryCache",
  "SPAStorage",
];

/**
 * Delete the user-data databases. Under iOS Lockdown Mode these are the only
 * copy that matters — deleting them deletes their native files. Any failure
 * rejects, so sign-out stops rather than redirecting with data left behind.
 *
 * The delete function is injectable for tests; production uses `Dexie.delete`.
 */
export async function purgeUserDataDatabases(
  deleteDatabase: (name: string) => Promise<void> = (name) =>
    Dexie.delete(name),
): Promise<void> {
  // Sequential, and it stops at the first failure — so the irreplaceable queue
  // is only ever touched once everything rebuildable is already gone.
  for (const name of USER_DATA_DATABASES_REBUILDABLE_FIRST) {
    await deleteDatabase(name);
  }
}
